import { Button, MagellanPopup } from "components/atoms";
import { useTranslation } from "react-i18next";
import { PiX, PiCheck } from "react-icons/pi";
import Popup from "reactjs-popup";
import { PopupActions } from "reactjs-popup/dist/types";
import { BUTTON_VARIANT } from "types/components/button/button.interface";
import { useRef } from "react";
import "./PopupCheckPermissions.scss";

interface IPopupCheckPermissionsProps {
  trigger?: JSX.Element;
  onCancel?: (action: PopupActions) => void;
  onUpdate?: (action: PopupActions, formValues?: any) => void;
  header: string;
  open?: boolean;
  onClose?: () => void;
  formValues?: any;
  requiredPermissions?: Array<{
    permissionName: string;
    flag: number;
    requiredPermissionName: string;
    requiredFlag: number;
  }>;
}

const PopupCheckPermissions = ({
  trigger,
  onCancel,
  onUpdate,
  header,
  open,
  onClose,
  formValues,
  requiredPermissions
}: IPopupCheckPermissionsProps) => {
  const { t } = useTranslation();

  const popupRef = useRef<PopupActions>(null);

  const handleCancel = () => {
    if (popupRef.current && onCancel) {
      onCancel(popupRef.current);
    }
  };

  const handleUpdate = () => {
    if (popupRef.current && onUpdate) {
      onUpdate(popupRef.current, formValues);
    }
  };

  const groupPermissions = () => {
    if (!requiredPermissions || requiredPermissions.length === 0) return {};

    return requiredPermissions.reduce((acc, permission) => {
      const domainName = permission.requiredPermissionName;
      if (!acc[domainName]) {
        acc[domainName] = [];
      }
      acc[domainName].push(permission);
      return acc;
    }, {} as Record<string, typeof requiredPermissions>);
  };

  const translatePermissionFlag = (domainName: string, flag: number) => {
    try {
      return t(`permissions.${domainName}.${flag}`);
    } catch {
      return `Flag ${flag}`;
    }
  };

  const translateDomainName = (domainName: string) => {
    try {
      return t(`permissions.domains.${domainName}`);
    } catch {
      return domainName;
    }
  };

  const groupedPermissions = groupPermissions();

  return (
    <Popup 
      ref={popupRef} 
      modal 
      nested 
      trigger={trigger}
      open={open}
      onClose={onClose}
    >
      <MagellanPopup className="max-w-md max-h-[80vh]">
        <MagellanPopup.Header className="justify-center w-full mb-2">
          {t(header)}
        </MagellanPopup.Header>
        
        <div className="popup-content p-4">
          <p className="text-gray-700 mb-8 text-center text-sm px-4">
            {t("permissions.popup.check-permissions-message-description")}
          </p>
          
          {Object.keys(groupedPermissions).length > 0 && (
            <div className="required-permissions-details mt-4 mb-4">
              <div className="permissions-list overflow-y-auto max-h-[calc(80vh-280px)]">
                {Object.entries(groupedPermissions).map(([domainName, permissions]) => (
                  <div key={domainName} className="permission-domain mb-3 p-3 bg-gray-50 rounded">
                    <h5 className="text-base font-medium text-gray-700 mb-2">
                      {translateDomainName(permissions[0]?.requiredPermissionName || '')} ({translatePermissionFlag(permissions[0]?.requiredPermissionName || '', permissions[0]?.requiredFlag || 0)}):
                    </h5>
                    <div className="permission-flags">
                      {permissions.map((permission, index) => (
                        <div key={index} className="permission-item text-sm text-gray-600 mb-1 pl-4">
                          • <span className="font-semibold">{translateDomainName(permission.permissionName)}</span> ({translatePermissionFlag(permission.permissionName, permission.flag)})
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {Object.keys(groupedPermissions).length > 0 && (
          <h4 className="text-gray-800 text-center px-4">
            {t("permissions.popup.check-permissions-message-footer")}
          </h4>
        )}
        
        <div className="popup-buttons-container">
          <Button
            type="button"
            variant={BUTTON_VARIANT.default}
            onClick={handleCancel}
            className="cancel-button"
          >
            <Button.Text text={t("button.cancel")} />
            <Button.Icon icon={<PiX />} />
          </Button>
          
          <Button
            type="button"
            variant={BUTTON_VARIANT.submit}
            onClick={handleUpdate}
            className="update-button"
          >
            <Button.Text text={t("button.save")} />
            <Button.Icon icon={<PiCheck />} />
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default PopupCheckPermissions;
